import { useEffect, useRef, useState } from "react"
import { Customer, useCustomersActions } from "@/api/customers"
import { SearchSelect } from "@/components/ux/search.select"
import { CustomersEditSheet } from "@/customers/customers-edit.sheet"

interface CustomerSelectProps {
  onChange: (value?: string | null) => void;
  value: string | undefined;
}

export default function CustomerSelect({
  onChange,
  value,
}: CustomerSelectProps) {
  const { search, get } = useCustomersActions()
  const [isSearching, setIsSearching] = useState(false)
  const [results, setResults] = useState<Map<string, Customer>>(new Map())
  const [addCustomer, setAddCustomer] = useState(false)
  const initialValueRef = useRef(value)

  useEffect(() => {
    (async () => {
      if (initialValueRef.current) {
        const customer = await get(initialValueRef.current)
        if (customer) {
          const newResults = new Map()
          newResults.set(customer.id, customer)
          setResults(newResults)
        }
      }
    })()
  }, [get])

  const onSearch = async (query: string) => {
    setIsSearching(true)
    setResults(new Map())
    const customers = await search(query)
    setIsSearching(false)
    const results = new Map<string, Customer>()
    customers.forEach((customer) => results.set(customer.id, customer))
    setResults(results)
  }

  const onAdded = (customer?: Customer) => {
    if (customer) {
      const newResults = new Map(results)
      newResults.set(customer.id, customer)
      setResults(newResults)
      onChange(customer.id)
    }
    setAddCustomer(false)
  }

  return (
    <>
      <SearchSelect
        onSearch={onSearch}
        onSelectResult={onChange}
        selectedResult={value}
        isSearching={isSearching}
        placeholder="Find or add customer..."
        results={Array.from(results.keys())}
        renderItem={(value) => {
          const customer = results.get(value)
          return customer ? (
            <div className="font-normal text-sm">
              {customer.firstName} {customer.lastName}
            </div>
          ) : null
        }}
        onAddItem={() => setAddCustomer(true)}
        addItemLabel="Add Customer"
      />
      <CustomersEditSheet id="create" open={addCustomer} onDone={onAdded} />
    </>
  )
}
